import { css } from "@emotion/css";
import { CalendarIcon } from "@octopusdeploy/design-system-icons";
import { space } from "@octopusdeploy/design-system-tokens";
import { addMonths, format, isValid, parse, subMonths } from "date-fns";
import { useRef, useState, useId } from "react";
import { Button } from "../../Button";
import { useDropdownButton } from "../../Dropdown";
import { Popover } from "../../Popover";
import { FormDescription } from "../Primitives/FormDescription";
import { Input } from "../Primitives/Input";
import { InputError } from "../Primitives/InputError";
import InputLabel from "../Primitives/InputLabel";
import type { TextFieldProps } from "../TextField";
import { DatePickerCalendar } from "./DatePickerCalendar";
export interface DatePickerNewProps extends Omit<TextFieldProps<Date>, "type" | "placeholder" | "onChange" | "prefix" | "suffix"> {
    /**
     * The callback when the selected date is changed and valid.
     * When the date is not valid such as manually typed in the input field, this callback will not be fired
     */
    onChange: (date: Date) => void;
    /**
     * The number of months in the past from today to use as the earliest selectable date
     */
    startDateInMonths?: number;
    /**
     * The number of months in the future from today to use as the last selectable date
     */
    endDateInMonths?: number;
    /**
     * Disable the dates before today in the Calendar
     */
    disablePastDates?: boolean;
}
export function DatePicker({ value, onChange, label, description, error, disabled, hasRequiredMarker, hasOptionalMarker, hasDefaultMarker, popover, autoFocus, readOnly, name, startDateInMonths = 24, endDateInMonths = 24, disablePastDates, }: DatePickerNewProps) {
    const baseId = useId();
    const inputId = `datepicker-${name ? `${name}-` : ""}${baseId}`;
    const inputContainerRef = useRef<HTMLDivElement>(null);
    const [inputValue, setInputValue] = useState(value ? format(value, "yyyy-MM-dd") : "");
    const [selectedDate, setSelectedDate] = useState<Date | undefined>(value);
    const [monthDisplayedInCalendar, setMonthDisplayedInCalendar] = useState<Date | undefined>(value);
    const dropdownButtonState = useDropdownButton({ trapFocus: true, dropdownAriaRole: "dialog" });
    const { isOpen, closeDropdown, setButtonRef, toggleDropdown, setDropdownRef, dropdownAttributes, buttonAriaAttributes } = dropdownButtonState;
    const handleInputChange = (newValue: string) => {
        setInputValue(newValue);
        // Use value from props as reference date, so that the timezone information is preserved.
        const parsedDate = parse(newValue, "yyyy-MM-dd", value ?? new Date());
        if (isValid(parsedDate)) {
            setSelectedDate(parsedDate);
            setMonthDisplayedInCalendar(parsedDate);
            onChange(parsedDate);
        }
        else {
            setSelectedDate(undefined);
        }
    };
    const toggleCalendar = () => {
        toggleDropdown();
    };
    const handleSelectDateInPicker = (date: Date) => {
        onChange(date);
        setSelectedDate(date);
        setMonthDisplayedInCalendar(date);
        setInputValue(format(date, "yyyy-MM-dd"));
        closeDropdown({ moveFocusToButton: true });
    };
    const calendarButton = <Button ref={setButtonRef} importance={"ghost"} icon={<CalendarIcon size={20}/>} onClick={toggleCalendar} disabled={disabled || readOnly} accessibleName="Toggle date picker's calendar" {...buttonAriaAttributes}/>;
    return (<div className={datePickerStyles.container}>
            <div>
                <InputLabel label={label} htmlFor={inputId} isDisabled={disabled} hasRequiredMarker={hasRequiredMarker} hasOptionalMarker={hasOptionalMarker} hasDefaultMarker={hasDefaultMarker} popover={popover}/>
                {description && <FormDescription id={`${inputId}-description`} description={description} isDisabled={disabled}/>}
            </div>
            <div ref={inputContainerRef}>
                <Input id={inputId} type={"text"} value={inputValue} onChange={handleInputChange} disabled={disabled} required={hasRequiredMarker} readOnly={readOnly} autoFocus={autoFocus} name={name} placeholder={"YYYY-MM-DD"} suffix={calendarButton} aria-invalid={!!error} aria-describedby={[description ? `${inputId}-description` : null, error ? `${inputId}-error` : null].filter(Boolean).join(" ") || undefined}/>
            </div>
            {isOpen && (<Popover placement={"bottom-start"} anchorEl={inputContainerRef.current} open={isOpen} onClose={closeDropdown} elementId={dropdownAttributes.dropdownId} accessibleName={"Date picker calendar"} accessibleRole={dropdownAttributes.dropdownAriaAttributes.role}>
                    <div ref={setDropdownRef}>
                        <DatePickerCalendar selected={selectedDate} onSelect={handleSelectDateInPicker} month={monthDisplayedInCalendar} onMonthChange={setMonthDisplayedInCalendar} startMonth={subMonths(new Date(), startDateInMonths)} endMonth={addMonths(new Date(), endDateInMonths)} disablePastDates={disablePastDates}/>
                    </div>
                </Popover>)}
            {error && <InputError error={error} id={`${inputId}-error`}/>}
        </div>);
}
const datePickerStyles = {
    container: css({
        display: "flex",
        flexDirection: "column",
        gap: space[8],
        maxWidth: "37.5rem",
        minWidth: "10.625rem",
    }),
};
